import useClevertapForSrp from '@/src/hooks/analytics/clevertap/srp'
import { useThemedToast } from '@/src/hooks/ui/useThemedToast'
import SwapHorizIcon from '@mui/icons-material/SwapHoriz'
import { Box, Button, ClickAwayListener, Divider, IconButton } from '@mui/material'
import { useTranslations } from 'next-intl'
import React, { useEffect, useState } from 'react'
import DatePicker from './CalendarWrapper/DatePicker'
import { FromToInput } from './fromToInput'
import { useSearchInputManager } from './hooks/search-inputs-manager'
import { TravelersInput } from './TravelersInput'
import { TripTypeSelector } from './tripTypeSelector'

interface InputsContainerProps {
    jsons?: Record<string, any>
    overlayOpen: () => void
    overlayClose: () => void
    srpResultsLoading: boolean
}

export const InputsContainer: React.FC<InputsContainerProps> = ({
    jsons,
    overlayOpen,
    overlayClose,
    srpResultsLoading
}) => {
    const {
        getAirportsFromQuery,
        fromToInputsState,
        onAirportSelect,
        handleSwap,
        isRoundTrip,
        range,
        isRoundTripDispatch,
        rangeDispatch,
        flightClassState,
        travelersState,
        handleTravelerChange,
        handleFlightClassChange,
        canIncreaseAdults,
        canIncreaseChildren,
        canIncreaseInfants,
        canDecreaseAdults,
        canDecreaseChildren,
        canDecreaseInfants,
        updateSearchParams
    } = useSearchInputManager(jsons, srpResultsLoading)

    const { showError } = useThemedToast()
    const t = useTranslations('common')
    // Define the possible active component types
    type ActiveComponent = 'triptype' | 'from' | 'to' | 'date' | 'travelers' | null
    const [activeComponent, setActiveComponent] = useState<ActiveComponent>(null)

    // Validation state for same airport error
    const [sameAirportError, setSameAirportError] = useState(false)

    // Check if from and to airports are the same
    useEffect(() => {
        const fromValue = fromToInputsState.from.value as string
        const toValue = fromToInputsState.to.value as string

        if (fromValue && toValue) {
            // Extract airport codes from the values
            const fromCode = fromValue.split(' - ')[0]
            const toCode = toValue.split(' - ')[0]

            setSameAirportError(fromCode === toCode && fromCode !== '')
        } else {
            setSameAirportError(false)
        }
    }, [fromToInputsState.from.value, fromToInputsState.to.value])

    useEffect(() => {
        if (activeComponent) {
            overlayOpen()
        } else {
            overlayClose()
        }
    }, [activeComponent, overlayOpen, overlayClose])

    // Close dropdown when SRP loading state changes (prevents Popper jumping to top-left after results load)
    useEffect(() => {
        setActiveComponent(null)
    }, [srpResultsLoading])

    // Centralized function to handle component activation
    const handleComponentActivation = (component: ActiveComponent) => {
        if (activeComponent === component && component !== 'from' && component !== 'to') {
            // If clicking the same component, close it (except for from/to inputs)
            setActiveComponent(null)
        } else {
            // If clicking a different component, switch to it
            setActiveComponent(component)
        }
    }

    // Function to close any active component
    const closeActiveComponent = () => {
        setActiveComponent(null)
    }
    const swapHandler = () => {
        setActiveComponent(null)
        handleSwap()
    }

    // Handle click away - this should close any active component
    const handleClickAway = (event: Event | React.SyntheticEvent) => {
        // Close any active component when clicking outside
        if (activeComponent) {
            setActiveComponent(null)
        }
    }

    const { triggerSrpEvent } = useClevertapForSrp()

    //HADNLE UPDATE while closing overlay
    const handleUpdate = () => {
        // Don't update if there's a validation error
        if (sameAirportError) {
            return
        }
        setActiveComponent(null)

        triggerSrpEvent('Air_UI_Action', {
            action_name: 'modify_search',
            action_type: 'BUTTON',
            action_value: 'N/A'
        })

        updateSearchParams()
    }

    // Helper functions to check if a specific component is active
    const isComponentActive = (component: ActiveComponent) => activeComponent === component

    useEffect(() => {
        if (sameAirportError) {
            showError(t('sameAirportError'))
        }
    }, [sameAirportError])
    return (
        <Box sx={{ position: 'relative', zIndex: 0 }}>
            <ClickAwayListener onClickAway={handleClickAway}>
                <Box
                    data-search-container
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        overflow: 'hidden',
                        px: 1,
                        py: 1,
                        bgcolor: '#ffffff',
                        border: '1px solid rgb(229 231 235)',
                        borderRadius: '0.75rem',
                        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
                        position: 'relative',
                        zIndex: 1
                    }}
                >
                    <TripTypeSelector
                        isRoundTrip={isRoundTrip}
                        onTripTypeChange={isRoundTripDispatch}
                        active={isComponentActive('triptype')}
                        onActivate={() => handleComponentActivation('triptype')}
                        onClose={closeActiveComponent}
                    />

                    {/* Origin */}
                    <FromToInput
                        srpResultsLoading={srpResultsLoading}
                        key={`${fromToInputsState.from.value}-from` as string}
                        action="from"
                        initialValue={fromToInputsState.from.value as string}
                        loading={fromToInputsState.from.loading}
                        optionListItems={fromToInputsState.from.options}
                        queryOptionListItems={getAirportsFromQuery}
                        onOptionSelect={onAirportSelect}
                        active={isComponentActive('from')}
                        onActivate={() => handleComponentActivation('from')}
                        onClose={closeActiveComponent}
                        hasError={sameAirportError}
                        isPopularAirports={fromToInputsState.from.isPopularAirports}
                    />

                    {/* Swap */}
                    <IconButton sx={{ mx: 1, bgcolor: '#f3f4f6' }} size="small" onClick={swapHandler}>
                        <SwapHorizIcon />
                    </IconButton>

                    {/* Destination */}
                    <FromToInput
                        key={`${fromToInputsState.to.value}-to` as string}
                        action="to"
                        initialValue={fromToInputsState.to.value as string}
                        loading={fromToInputsState.to.loading}
                        optionListItems={fromToInputsState.to.options}
                        queryOptionListItems={getAirportsFromQuery}
                        onOptionSelect={onAirportSelect}
                        active={isComponentActive('to')}
                        onActivate={() => handleComponentActivation('to')}
                        onClose={closeActiveComponent}
                        hasError={sameAirportError}
                        isPopularAirports={fromToInputsState.to.isPopularAirports}
                    />

                    <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

                    {/* Date */}
                    <DatePicker
                        isRoundTrip={isRoundTrip}
                        range={range}
                        dateClick={rangeDispatch}
                        active={isComponentActive('date')}
                        onActivate={() => handleComponentActivation('date')}
                        onClose={closeActiveComponent}
                    />

                    <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            cursor: 'pointer',
                            minWidth: 165,
                            maxWidth: 165,
                            overflow: 'hidden'
                        }}
                    >
                        <TravelersInput
                            adults={travelersState.adults}
                            childs={travelersState.childs}
                            infants={travelersState.infants}
                            flightClass={flightClassState as string}
                            onTravelerChange={handleTravelerChange}
                            onFlightClassChange={handleFlightClassChange}
                            canIncreaseAdults={canIncreaseAdults(travelersState)}
                            canIncreaseChildren={canIncreaseChildren(travelersState)}
                            canIncreaseInfants={canIncreaseInfants(travelersState)}
                            canDecreaseAdults={canDecreaseAdults(travelersState)}
                            canDecreaseChildren={canDecreaseChildren(travelersState)}
                            canDecreaseInfants={canDecreaseInfants(travelersState)}
                            active={isComponentActive('travelers')}
                            onActivate={() => handleComponentActivation('travelers')}
                            onClose={closeActiveComponent}
                        />
                    </Box>

                    <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

                    {/* Update Button */}
                    <Box sx={{ px: 2 }}>
                        <Button
                            disabled={srpResultsLoading}
                            variant="contained"
                            color="primary"
                            onClick={handleUpdate}
                        >
                            {t('update')}
                        </Button>
                    </Box>
                </Box>
            </ClickAwayListener>
        </Box>
    )
}
